import React from 'react';
import type { MealBreakConfig, RateMultiplier } from '@/types';
import { MealHourlyPremiumTimeline } from '@/components/config_forms/shared/help-graphics/MealHourlyPremiumTimeline';
import { MealHourlyPremiumOTSection } from '@/components/config_forms/shared/help-graphics/MealHourlyPremiumOTSplit';
import { MealHourlyPremiumOTInteraction } from '@/components/config_forms/shared/help-graphics/MealHourlyPremiumOTInteraction';
import { CountModeHourlyPremiumGraphic } from '@/components/config_forms/shared/help-graphics/CountModeHourlyPremiumGraphic';
import { CountModeHourlyPremiumOTSection } from '@/components/config_forms/shared/help-graphics/CountModeHourlyPremiumOTSplit';

interface MealHourlyPremiumInjectableParams {
  mealBreakConfig: MealBreakConfig;
  rateMultipliers: RateMultiplier[];
  onMealConfigChange?: (updates: Partial<MealBreakConfig>) => void;
  thresholdFromBilling?: number | null;
}

export function buildMealHourlyPremiumInjectable({
  mealBreakConfig,
  rateMultipliers,
  onMealConfigChange,
  thresholdFromBilling = null,
}: MealHourlyPremiumInjectableParams): {
  injectables: Record<string, React.ReactNode>;
} {
  // Ladder: sort by multiplier ascending with a threshold tiebreaker for
  // equal-multiplier entries (lower threshold = sooner activation = comes first).
  // This shows the user's complete configured ladder rather than filtering
  // based on backend's runtime selection rules, since the graphic is an
  // illustration of the config, not a preview of specific shift math.
  const ladder = rateMultipliers
    .filter((m) => m.defaultMultiplier != null)
    .map((m) => ({
      code: m.code,
      name: m.name,
      multiplier: m.defaultMultiplier!,
      thresholdHours:
        m.defaultPerShiftThresholdHours ?? m.defaultPerWeekThresholdHours ?? null,
    }))
    .sort((a, b) => {
      if (a.multiplier !== b.multiplier) return a.multiplier - b.multiplier;
      const aT = a.thresholdHours ?? Infinity;
      const bT = b.thresholdHours ?? Infinity;
      return aT - bT;
    });

  // Coercion (view-only, never written back to config)
  const rawDeadline = mealBreakConfig.mealDeadlineHours;
  const deadlineHours =
    rawDeadline == null || rawDeadline <= 0 || Number.isNaN(rawDeadline) ? 5 : rawDeadline;

  const rawType = mealBreakConfig.premiumType;
  const premiumType: 'fixed' | 'percentage' =
    rawType === 'fixed' || rawType === 'percentage' ? rawType : 'fixed';

  // When premiumValue is unset (user hasn't typed into PREMIUM AMOUNT yet), fall
  // back to the same illustrative defaults the markdown config strip uses
  // (markdownVariables.ts: `meal_premium_value || 15` for fixed, `|| 10` for
  // percentage). Keeping the two in lockstep means the "Hourly Premium: $15/hr"
  // line in the config strip and the zone rate label in the graphic show the
  // same number instead of diverging.
  const rawValue = mealBreakConfig.premiumValue;
  const illustrativeDefault = premiumType === 'percentage' ? 10 : 15;
  const premiumValue =
    rawValue == null || Number.isNaN(rawValue) ? illustrativeDefault : rawValue;

  // premiumBasis is overloaded with credit_extra_time's rate-multiplier-code semantics.
  // Only 'ot_inclusive' is meaningful for hourly premium's compounding mode;
  // anything else (including 'STRAIGHT_TIME' from default seed state) coerces to 'base_rate'.
  const rawBasis = mealBreakConfig.premiumBasis;
  const premiumBasis: 'base_rate' | 'ot_inclusive' =
    rawBasis === 'ot_inclusive' ? 'ot_inclusive' : 'base_rate';

  const timelineElement = React.createElement(MealHourlyPremiumTimeline, {
    deadlineHours,
    recurringDeadline: mealBreakConfig.recurringMealDeadline ?? false,
    premiumType,
    premiumValue,
    premiumBasis,
    ladder,
    ...(onMealConfigChange
      ? {
          onPremiumTypeChange: (type: 'fixed' | 'percentage') =>
            onMealConfigChange({ premiumType: type }),
          onPremiumBasisChange: (basis: 'base_rate' | 'ot_inclusive') =>
            onMealConfigChange({ premiumBasis: basis }),
          onPremiumValueChange: (value: number | null) =>
            onMealConfigChange({ premiumValue: value }),
        }
      : {}),
  });

  const otSplitElement = React.createElement(MealHourlyPremiumOTSection, {
    premiumType,
    premiumValue,
    premiumBasis,
    ladder,
  });

  const otInteractionElement = premiumBasis === 'ot_inclusive'
    ? React.createElement(MealHourlyPremiumOTInteraction, {
        premiumType,
        premiumValue,
        premiumBasis,
        recurringDeadline: mealBreakConfig.recurringMealDeadline ?? false,
        ladder,
      })
    : null;

  // Count-mode coercion
  const rawStacking = mealBreakConfig.premiumStacking;
  const premiumStacking: 'flat' | 'stacked' =
    rawStacking === 'stacked' ? 'stacked' : 'flat';

  const requiredBreaks = mealBreakConfig.requiredBreaks ?? 1;
  const requiredBreaksHalfDay = mealBreakConfig.requiredBreaksHalfDay ?? null;
  const thresholdHours = mealBreakConfig.thresholdHours ?? null;
  const additionalBreakIntervalHours = mealBreakConfig.additionalBreakIntervalHours ?? null;

  const countGraphicElement = React.createElement(CountModeHourlyPremiumGraphic, {
    premiumType,
    premiumValue,
    premiumBasis,
    premiumStacking,
    requiredBreaks,
    requiredBreaksHalfDay,
    thresholdHours,
    thresholdFromBilling,
    additionalBreakIntervalHours,
    ...(onMealConfigChange
      ? {
          onPremiumTypeChange: (type: 'fixed' | 'percentage') =>
            onMealConfigChange({ premiumType: type }),
          onPremiumBasisChange: (basis: 'base_rate' | 'ot_inclusive') =>
            onMealConfigChange({ premiumBasis: basis }),
          onPremiumValueChange: (value: number | null) =>
            onMealConfigChange({ premiumValue: value }),
          onPremiumStackingChange: (stacking: 'flat' | 'stacked') =>
            onMealConfigChange({ premiumStacking: stacking }),
        }
      : {}),
  });

  const countOTSplitElement = React.createElement(CountModeHourlyPremiumOTSection, {
    premiumType,
    premiumValue,
    premiumBasis,
    ladder,
  });

  return {
    injectables: {
      meal_hourly_premium_timeline: timelineElement,
      meal_hourly_premium_ot_split: otSplitElement,
      meal_hourly_premium_ot_interaction: otInteractionElement,
      meal_hourly_premium_count: countGraphicElement,
      meal_hourly_premium_count_ot_split: countOTSplitElement,
    },
  };
}
